import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Paper, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getFanPollResults } from './fanPollApi';

const ResultRow = ({ label, voteCount, percentage }) => (
    <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2" fontWeight={700}>{voteCount} ({percentage.toFixed(1)}%)</Typography>
        </Box>
        <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, percentage))} sx={{ height: 9, borderRadius: 2 }} />
    </Box>
);

export default function FanPollResults({ pollId, refreshKey }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        if (!pollId) return;
        setLoading(true);
        setError('');
        try {
            setResults(await getFanPollResults(pollId));
        } catch (requestError) {
            setError(requestError.message ?? String(requestError));
        } finally {
            setLoading(false);
        }
    }, [pollId]);

    useEffect(() => { load(); }, [load, refreshKey]);

    if (!pollId) return <Alert severity="info">Save the poll before viewing results.</Alert>;

    return (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h6">Results</Typography>
                    <Typography color="text.secondary">{results?.totalVotes ?? 0} total votes</Typography>
                </Box>
                <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Box>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && results?.totalVotes === 0 && <Alert severity="info">No votes yet.</Alert>}
            {!loading && !error && (results?.options ?? []).map(option => (
                <ResultRow key={option.optionId} label={option.label} voteCount={option.voteCount} percentage={option.percentage} />
            ))}
            {!loading && !error && (results?.suggestions ?? []).length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Approved suggestions</Typography>
                    {results.suggestions.map(suggestion => (
                        <ResultRow key={suggestion.suggestionId} label={suggestion.suggestionText}
                            voteCount={suggestion.voteCount} percentage={suggestion.percentage} />
                    ))}
                </Box>
            )}
        </Paper>
    );
}
